import type { FindingsReport } from '../../lib/api.ts'
import { formatCount } from '../../lib/findingsReport.ts'
import { Button } from '../Button.tsx'
import { FindingsHelp, type FindingsHelpContent } from './FindingsHelp.tsx'

function UtilizationMetric({
  label,
  count,
  denominator,
  denominatorLabel,
  historyComplete,
  noFindings,
  help,
  unrecorded,
}: {
  label: string
  count: number
  denominator: number
  denominatorLabel: { singular: string; plural: string }
  historyComplete: boolean
  noFindings: boolean
  help: FindingsHelpContent
  /**
   * Conferências the measure cannot speak for, said beside the number instead
   * of withholding it: for a measure whose gap is a known stretch of time
   * rather than a hole anywhere in the history.
   */
  unrecorded?: string | undefined
}) {
  const measurable = !noFindings && denominator > 0 && (historyComplete || unrecorded !== undefined)
  const percentage = measurable ? Math.round((count / denominator) * 100) : undefined
  const denominatorText = `${formatCount(denominator)} ${denominator === 1 ? denominatorLabel.singular : denominatorLabel.plural}`

  return (
    <article className="border-stroke-weak flex min-w-0 flex-col gap-3 rounded-xl border p-4">
      <h3 className="text-tPSemi text-text-strong">
        <FindingsHelp label={label} content={help} />
      </h3>
      <div>
        {noFindings && historyComplete ? (
          <p className="text-tSmall text-text-weak">
            Sem conferências com apontamentos neste recorte.
          </p>
        ) : measurable ? (
          <>
            <p className="text-tSmall text-text-weak">
              <strong className="text-tH3 text-text-strong">{formatCount(count)}</strong>{' '}
              {count === 1 ? 'conferência' : 'conferências'}{' '}
              <span className="text-text-strong">· {percentage}%</span>
            </p>
            <p className="text-tTiny text-text-weak mt-1">de {denominatorText}</p>
            {!historyComplete && unrecorded !== undefined && (
              <p className="text-tTiny text-text-weak mt-1">{unrecorded}</p>
            )}
          </>
        ) : (
          <>
            <p className="text-tP text-text-strong">Histórico incompleto</p>
            {count > 0 && (
              <p className="text-tTiny text-text-weak mt-1">
                {formatCount(count)} {count === 1 ? 'caso identificado' : 'casos identificados'}; o
                total pode ser maior.
              </p>
            )}
            <p className="text-tTiny text-text-weak mt-1">
              O total e o percentual não podem ser determinados para este período.
            </p>
          </>
        )}
      </div>
      {measurable && percentage !== undefined && (
        <div
          role="img"
          aria-label={`${label}: ${formatCount(count)} de ${denominatorText}; ${percentage}%`}
          className="bg-fill-brand-weak h-2 overflow-hidden rounded-full"
        >
          <div
            aria-hidden="true"
            className="bg-fill-brand h-full rounded-full"
            style={{ width: `${percentage}%` }}
          />
        </div>
      )}
    </article>
  )
}

export function FindingsAttention({
  report,
  onOpen,
}: {
  report: FindingsReport
  onOpen: () => void
}) {
  const usage = report.productionUsage
  const hasProduction = usage.produced > 0
  if (!hasProduction && report.engagement.closedWithUnanswered === 0) return null

  const findingsComplete = usage.findingsUnknown === 0
  const responsesComplete = findingsComplete && usage.responsesUnknown === 0
  const conclusionComplete = findingsComplete && usage.conclusionUnknown === 0
  const noFindings = findingsComplete && usage.withFindingsKnown === 0

  const coverageHelp = `Cobertura no marco da primeira produção: ${formatCount(usage.withFindingsKnown)} conferências com apontamentos conhecidos, ${formatCount(usage.withoutFindingsKnown)} sem apontamentos e ${formatCount(usage.findingsUnknown)} com histórico de apontamentos indeterminado. Históricos de respostas indeterminados: ${formatCount(usage.responsesUnknown)}; de conclusão: ${formatCount(usage.conclusionUnknown)}.`
  const responseHelp: FindingsHelpContent = {
    calculation:
      'Numerador: conferências com apontamentos sem resposta operacional salva na primeira ida registrada à produção. Denominador: total de conferências produzidas com apontamentos no período e histórico de respostas completo.',
    source:
      'Respostas operacionais registradas até a primeira ida à produção após a conferência ficar disponível para verificação. O período filtra a data dessa primeira ida; uma ida posterior não cria uma nova primeira ida.',
    includes: responsesComplete
      ? noFindings
        ? 'Não houve conferências com apontamentos neste recorte.'
        : usage.withoutResponses > 0
          ? `A base completa tem ${formatCount(usage.responsesKnown)} conferências; ${formatCount(usage.withoutResponses)} ficaram sem resposta.`
          : `Nenhuma das ${formatCount(usage.responsesKnown)} conferências da base completa ficou sem resposta.`
      : usage.withoutResponses > 0
        ? `${formatCount(usage.withoutResponses)} casos sem resposta foram identificados nos registros disponíveis; isso não estima o total.`
        : 'Nenhum caso sem resposta foi identificado nos registros disponíveis; isso não comprova ausência no universo.',
    excludes: `${formatCount(usage.responsesUnknown)} conferências com apontamentos têm histórico de respostas indeterminado. ${coverageHelp}`,
    provisional: responsesComplete
      ? 'A cobertura está completa para este recorte. A lista de atenção abaixo segue a data de entrada na conferência e inclui pendências parciais; ela não detalha esta contagem.'
      : 'Conferências com movimentação registrada depois da primeira ida à produção têm histórico indeterminado. Casos identificados não estimam o total e não permitem calcular uma taxa.',
  }

  const openingHelp: FindingsHelpContent = {
    calculation:
      'Numerador: conferências que foram para a produção sem que ninguém da farmácia as abrisse antes. Denominador: total de conferências produzidas no período com registro de abertura, com ou sem apontamentos.',
    source:
      'Primeira abertura da conferência por alguém da farmácia, comparada com a primeira ida registrada à produção. Abrir depois que a requisição já saiu não conta como abertura.',
    includes:
      usage.openingKnown === 0
        ? 'Nenhuma conferência produzida no período tem registro de abertura.'
        : usage.withoutOpening > 0
          ? `Das ${formatCount(usage.openingKnown)} conferências com registro, ${formatCount(usage.withoutOpening)} não foram abertas antes de sair.`
          : `Nenhuma das ${formatCount(usage.openingKnown)} conferências com registro saiu sem ser aberta.`,
    excludes: `${formatCount(usage.openingUnknown)} conferências que já estavam com a farmácia antes de 09/10/2026, quando a abertura passou a ser registrada. Aberturas por super admin não contam.`,
    provisional:
      'Esta medida inclui conferências sem apontamentos, e por isso não tem a mesma base das outras duas.',
  }

  const conclusionHelp: FindingsHelpContent = {
    calculation:
      'Numerador: conferências com apontamentos sem conclusão registrada na primeira ida à produção. Denominador: total de conferências produzidas com apontamentos no período e histórico de conclusão completo.',
    source:
      'Histórico de etapas até a primeira ida à produção após a conferência ficar disponível para verificação. O período filtra a data dessa primeira ida; uma ida posterior não cria uma nova primeira ida.',
    includes: conclusionComplete
      ? noFindings
        ? 'Não houve conferências com apontamentos neste recorte.'
        : usage.withoutConclusion > 0
          ? `A base completa tem ${formatCount(usage.conclusionKnown)} conferências; ${formatCount(usage.withoutConclusion)} ficaram sem conclusão.`
          : `Nenhuma das ${formatCount(usage.conclusionKnown)} conferências da base completa ficou sem conclusão.`
      : usage.withoutConclusion > 0
        ? `${formatCount(usage.withoutConclusion)} casos sem conclusão foram identificados nos registros disponíveis; isso não estima o total.`
        : 'Nenhum caso sem conclusão foi identificado nos registros disponíveis; isso não comprova ausência no universo.',
    excludes: `${formatCount(usage.conclusionUnknown)} conferências com apontamentos têm histórico de conclusão indeterminado. ${coverageHelp}`,
    provisional: conclusionComplete
      ? 'A cobertura está completa para este recorte. Esta medida pode se sobrepor à de respostas sem registro; elas não formam um funil nem classificam a IA.'
      : 'Conferências com movimentação registrada depois da primeira ida à produção têm histórico indeterminado. Casos identificados não estimam o total e não permitem calcular uma taxa.',
  }

  return (
    <section
      aria-labelledby="findings-utilization-title"
      className="border-stroke-weak bg-background-raised flex flex-col gap-4 rounded-2xl border p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="findings-utilization-title" className="text-tPSemi text-text-strong">
            Utilização da conferência
          </h2>
          <p className="text-tSmall text-text-weak mt-1">
            Situação registrada na primeira ida à produção; mede uso do processo, não qualidade da
            IA.
          </p>
        </div>
        <span className="border-stroke-weak bg-background-base text-tTiny text-text-weak rounded-full border px-2.5 py-1">
          Ida à produção no período
        </span>
      </div>

      {hasProduction ? (
        <div className="grid min-w-0 gap-3 md:grid-cols-2 xl:grid-cols-3">
          <UtilizationMetric
            label="Produzidas sem abertura"
            count={usage.withoutOpening}
            denominator={usage.openingKnown}
            denominatorLabel={{
              singular: 'conferência produzida com registro de abertura',
              plural: 'conferências produzidas com registro de abertura',
            }}
            historyComplete={usage.openingUnknown === 0}
            noFindings={false}
            help={openingHelp}
            unrecorded={`${formatCount(usage.openingUnknown)} anteriores ao registro de abertura (09/10/2026) ficam fora.`}
          />
          <UtilizationMetric
            label="Produzidas sem resposta"
            count={usage.withoutResponses}
            denominator={usage.responsesKnown}
            denominatorLabel={{
              singular: 'conferência produzida com apontamentos e histórico de respostas conhecido',
              plural: 'conferências produzidas com apontamentos e histórico de respostas conhecido',
            }}
            historyComplete={responsesComplete}
            noFindings={noFindings}
            help={responseHelp}
          />
          <UtilizationMetric
            label="Produzidas sem conclusão"
            count={usage.withoutConclusion}
            denominator={usage.conclusionKnown}
            denominatorLabel={{
              singular: 'conferência produzida com apontamentos e histórico de conclusão conhecido',
              plural: 'conferências produzidas com apontamentos e histórico de conclusão conhecido',
            }}
            historyComplete={conclusionComplete}
            noFindings={noFindings}
            help={conclusionHelp}
          />
        </div>
      ) : (
        <p className="text-tSmall text-text-weak">Nenhuma ida à produção registrada no período.</p>
      )}

      <div className="border-stroke-weak flex flex-wrap items-center justify-between gap-3 border-t pt-3">
        <p className="text-tTiny text-text-weak">
          Na lista de atenção por entrada na conferência,{' '}
          {formatCount(report.engagement.closedWithUnanswered)} conferências têm ao menos um
          apontamento sem resposta.
        </p>
        <Button variant="secondary" tone="neutral" size="small" onClick={onOpen}>
          Ver pendências · entrada na conferência
        </Button>
      </div>
    </section>
  )
}
